import { Button, Box, CircularProgress, Typography, IconButton } from '@mui/material';
import { AttachFile, Close, PhotoCamera } from '@mui/icons-material';
import React, { useRef, useState } from 'react';

import { Folders } from '../../../../../../@types/uploaded-files.type';
import { uploadFiles } from '../../../../../../api/uploadedFiles';
import { ServiceAttachmentUploadFeature } from '../../../../../../configs/enums';

interface MobileFileUploadFieldProps {
  title: string;
  isRequired: boolean;
  isPhoto: boolean;
  serviceId: number;
  value: any;
  onChange: (value: any) => void;
}

export const MobileFileUploadField: React.FC<MobileFileUploadFieldProps> = ({
  title,
  isRequired,
  isPhoto,
  serviceId,
  value,
  onChange,
}) => {
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState<string>(value?.fileName || '');
  const [uploadError, setUploadError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadError('');

    try {
      const formData = new FormData();
      formData.append('fileList', files[0]);

      const featureType = isPhoto
        ? ServiceAttachmentUploadFeature.InteractionPhotoUpload
        : ServiceAttachmentUploadFeature.InteractionFileUpload;
      const response = await uploadFiles(serviceId, formData, Folders.ServiceAttachments, featureType);
      const uploadedFile = response?.['0'] || response;

      setFileName(files[0].name);
      onChange({
        fileName: files[0].name,
        fileKey: uploadedFile?.key || uploadedFile?.fileName,
        fileId: uploadedFile?.id,
      });
    } catch (err) {
      setUploadError('Upload failed. Please try again.');
      console.error('File upload failed:', err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClear = () => {
    setFileName('');
    onChange('');
  };

  if (fileName) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {isPhoto ? (
          <PhotoCamera fontSize="small" color="primary" />
        ) : (
          <AttachFile fontSize="small" color="primary" />
        )}
        <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {fileName}
        </Typography>
        <IconButton size="small" onClick={handleClear} aria-label="Remove file">
          <Close fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept={isPhoto ? 'image/*' : undefined}
        capture={isPhoto ? 'environment' : undefined}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        id={`file-upload-${title}`}
      />
      <Button
        variant="outlined"
        size="small"
        fullWidth
        disabled={uploading}
        onClick={() => fileInputRef.current?.click()}
        startIcon={
          uploading ? <CircularProgress size={16} /> : isPhoto ? <PhotoCamera /> : <AttachFile />
        }
      >
        {uploading
          ? 'Uploading...'
          : `${isPhoto ? 'Take Photo / Choose' : 'Choose File'}${isRequired ? ' *' : ''}`}
      </Button>
      {uploadError && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
          {uploadError}
        </Typography>
      )}
    </Box>
  );
};
